import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { Spin } from 'antd';
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react-lite';
import { Icon } from 'Components';

// External and Global Component Imports
import Header from '../PhishingManager/components/Header/index';
import EmptyState from '../PhishingManager/components/EmptyState';
import PaginationElement from 'PhishingManager/components/Pagination';
import InfinityScrollWrapper from 'Components/InfinityScroll/InfinityScrollWrapper';
import DebounceInput from '../PhishingManager/components/DebouncedInput';

// Helpers, Hooks, Stores and Services
import { errorMessageHandler } from '../PhishingManager/utils';
import { useIsMobile } from 'Hooks';
import { useStores } from 'Hooks/useStore';
import {
  getPhishingCampaignTemplates,
  getPhishingCampaignCategories,
  getPhishingCampaignTemplatePreviewDetails,
  getPhishingCampaignFilters
} from '../PhishingManager/service/index.Service';
import { TemplateDetails } from 'PhishingManager/pages/CampaignSteps';
import {
  ToastType,
  useDashboardContext
} from 'Pages/Dashboard/DashboardContext';

// Local Modular Sub-Components and Types
import StatusTabSwitcher, { StatusTab } from './components/StatusTabSwitcher';
import TemplateCard from '../PhishingManager/components/TemplateCard';
import TemplateListCard from './components/TemplateListCard';
import CategoryCard, { CategoryData } from './components/CategoryCard';
import PublishedFilterToolbar, { SortOrder, ViewMode } from './components/PublishedFilterToolbar';
import LibraryModal from './components/LibraryModal';
import PrefilledForm from './components/PrefilledForm';

// Styles
import styles from './styles/PhishingTemplateManager.module.scss';
import '../PhishingManager/styles/PhishingManager.module.scss';

export interface TemplateData {
  id: number | string;
  title: string;
  languagesCount: number;
  difficulty: string;
  img_path: string;
  categories?: string[];
}

interface ApiTemplateResponse {
  id: number;
  name: string;
  img_path: string;
  difficulties?: string;
  difficulty?: string;
  language_count?: number;
  languages?: string[];
  categories?: string[];
}

const PAGE_SIZE = 12;

const PhishingTemplateManager: React.FC = observer(() => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const { authStore, systemsStore } = useStores();
  const { selectedTenant } = systemsStore;
  const { setToastInfo } = useDashboardContext();

  const currentUser = authStore?.currentUser;
  const isSuperAdmin = Boolean(authStore?.isCurrentUserSuperAdmin);

  const [activeTab, setActiveTab] = useState<StatusTab>(() => (isSuperAdmin ? 'unpublished' : 'published'));
  const [debouncedSearch, setDebouncedSearch] = useState<string>('');

  const [selectedLanguageId, setSelectedLanguageId] = useState<number | null>(null);
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');
  const [viewLayoutMode, setViewLayoutMode] = useState<ViewMode>('grid');

  const [templates, setTemplates] = useState<TemplateData[]>([]);
  const [categories, setCategories] = useState<CategoryData[]>([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null);
  
  const [availableLanguages, setAvailableLanguages] = useState<{id: number, name: string}[]>([]);

  const [totalTemplates, setTotalTemplates] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [loading, setLoading] = useState<boolean>(false);

  const [viewMode, setViewMode] = useState<'list' | 'edit_form'>('list');

  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [modalLoading, setModalLoading] = useState<boolean>(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | string | null>(null);
  const [templateDetails, setTemplateDetails] = useState<TemplateDetails | null>(null);

  const filtersFetchedRef = useRef<boolean>(false);
  const fetchedCategoriesRef = useRef<CategoryData[] | null>(null);
  const isFetchingRef = useRef<boolean>(false);
  const hasErrorRef = useRef<boolean>(false);

  const setToast = useCallback((value: ToastType) => {
    if (setToastInfo) setToastInfo(value);
  }, [setToastInfo]);

  const tenantIdToPass = useMemo(() => {
    if (isSuperAdmin) {
      if (activeTab === 'unpublished' || selectedCategoryId === null) return 0;
      return selectedTenant.value;
    }
    const userGroupId = currentUser?.groups?.[0]?.id;
    const userTenantId = (currentUser as any)?.venza_tenant_id;

    return userGroupId ?? userTenantId ?? 0;
  }, [currentUser, isSuperAdmin, activeTab, selectedTenant, selectedCategoryId]);

  const selectedCategoryObj = useMemo(() => {
    return categories.find((cat) => cat.id === selectedCategoryId);
  }, [categories, selectedCategoryId]);

  const fetchFilters = useCallback(async () => {
    if (filtersFetchedRef.current) return;
    try {
      const response = await getPhishingCampaignFilters(0, '');
      if (response && response.data && response.data.languages) {
        setAvailableLanguages(response.data.languages);
        filtersFetchedRef.current = true;
      } else if (Array.isArray(response?.data)) {
        setAvailableLanguages(response.data);
        filtersFetchedRef.current = true;
      }
    } catch (error) {
      console.error('Failed to fetch filters:', error);
      setAvailableLanguages([]);
      filtersFetchedRef.current = true;
    }
  }, []);

  const fetchCategories = useCallback(async () => {
    if (fetchedCategoriesRef.current !== null) {
      setCategories(fetchedCategoriesRef.current);
      return;
    }

    try {
      setLoading(true);
      const res = await getPhishingCampaignCategories(tenantIdToPass);
      let categoriesData: CategoryData[] = [];

      if (res && res.data) {
        categoriesData = res.data;
      } else if (Array.isArray(res)) {
        categoriesData = res;
      }

      setCategories(categoriesData);
      fetchedCategoriesRef.current = categoriesData;
    } catch (error: any) {
      const toastMessage = errorMessageHandler(error, 'phishingManager.errors.fetchCategories');
      setToast({ message: t(toastMessage), type: 'warning' });
      setCategories([]);
    } finally {
      setLoading(false);
    }
  }, [tenantIdToPass, t, setToast]);

  const fetchTemplates = useCallback(async () => {
    if (isFetchingRef.current || hasErrorRef.current) return;

    try {
      isFetchingRef.current = true;
      setLoading(true);
      const statusParam = activeTab === 'unpublished' ? 'inactive' : 'active';

      const languageFilters =
        activeTab === 'published' && selectedCategoryId !== null && selectedLanguageId
          ? [selectedLanguageId]
          : undefined;

      const response = await getPhishingCampaignTemplates(tenantIdToPass, {
        name: debouncedSearch && debouncedSearch.trim().length > 0 ? debouncedSearch : undefined,
        status: statusParam,
        categories: selectedCategoryId ? [selectedCategoryId] : undefined,
        languages: languageFilters,
        sort: 'name',
        order: sortOrder,
        page: currentPage,
        limit: PAGE_SIZE
      });

      if (response && response.data) {
        let mappedData: TemplateData[] = response.data.map((item: ApiTemplateResponse) => {
          let templateCategories: string[] = [];
          if (Array.isArray(item.categories) && item.categories.length > 0) {
            templateCategories = item.categories;
          } else if (selectedCategoryObj?.name) {
            templateCategories = [selectedCategoryObj.name];
          }

          return {
            id: item.id,
            title: item.name,
            languagesCount: item.language_count ?? item.languages?.length ?? 1,
            difficulty: item.difficulties || item.difficulty || t('phishingTemplateManager.intermediate'),
            img_path: item.img_path,
            categories: templateCategories
          };
        });

        mappedData = mappedData.sort((a, b) =>
          sortOrder === 'asc' ? a.title.localeCompare(b.title) : b.title.localeCompare(a.title)
        );

        setTemplates((prev) => {
          if (!isMobile || currentPage === 1) {
            return mappedData;
          }
          return [...prev, ...mappedData];
        });

        if (response.meta) {
          setTotalTemplates(response.meta.total);
        }
      }
    } catch (error: any) {
      hasErrorRef.current = true;
      const toastMessage = errorMessageHandler(error, 'phishingManager.errors.fetchTemplates');
      setToast({ message: t(toastMessage), type: 'warning' });
      setTemplates([]);
      setTotalTemplates(0);
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  }, [
    activeTab,
    selectedCategoryId,
    selectedLanguageId,
    debouncedSearch,
    sortOrder,
    currentPage,
    tenantIdToPass,
    isMobile,
    t,
    setToast,
    selectedCategoryObj
  ]);

  const handleReviewClick = async (templateId: string | number, selectedLanguageIdParam?: number) => {
    if (!templateId) return;

    try {
      setSelectedTemplateId(templateId);
      setModalVisible(true);
      setModalLoading(true);

      const statusParam = activeTab === 'unpublished' ? 'inactive' : 'active';
      const isEditParam = isSuperAdmin && activeTab === 'unpublished' ? 1 : null;

      const res = await getPhishingCampaignTemplatePreviewDetails(
        tenantIdToPass,
        templateId,
        selectedLanguageIdParam ?? null,
        isEditParam,
        statusParam
      );

      if (res && res.data) {
        setTemplateDetails(res.data);
      } else if (res) {
        setTemplateDetails(res as any);
      }
    } catch (error: any) {
      const toastMessage = errorMessageHandler(error, 'phishingManager.errors.fetchTemplateDetails');
      setToast({ message: t(toastMessage), type: 'warning' });
      setTemplateDetails(null);
    } finally {
      setModalLoading(false);
    }
  };

  const handleDirectEditClick = async (templateId: string | number) => {
    if (!templateId) return;

    try {
      setLoading(true);
      setSelectedTemplateId(templateId);
      setModalVisible(false);

      const statusParam = activeTab === 'unpublished' ? 'inactive' : 'active';
      const isEditParam = isSuperAdmin && activeTab === 'unpublished' ? 1 : null;

      const res = await getPhishingCampaignTemplatePreviewDetails(
        tenantIdToPass,
        templateId,
        null,
        isEditParam,
        statusParam
      );

      if (res && res.data) {
        setTemplateDetails(res.data);
      } else if (res) {
        setTemplateDetails(res as any);
      }
      setViewMode('edit_form');
    } catch (error: any) {
      const toastMessage = errorMessageHandler(error, 'phishingManager.errors.fetchTemplateDetails');
      setToast({ message: t(toastMessage), type: 'warning' });
      setTemplateDetails(null);
    } finally {
      setLoading(false);
    }
  };

  const handleLanguageChange = (languageId: number) => {
    if (selectedTemplateId) {
      handleReviewClick(selectedTemplateId, languageId);
    }
  };

  const handleCloseModal = () => {
    setModalVisible(false);
    setTemplateDetails(null);
    setSelectedTemplateId(null);
  };

  const handleSaveAndEdit = () => {
    setModalVisible(false);
    setViewMode('edit_form');
  };

  const handleBackToList = (toastPayload?: ToastType) => {
    setViewMode('list');
    setTemplateDetails(null);
    setSelectedTemplateId(null);
    if (toastPayload && toastPayload.message) {
      setToast(toastPayload);
    }
  };

  const handleTabChange = (tab: StatusTab) => {
    if (tab === activeTab) return;
    setActiveTab(tab);
    setTemplates([]);
    setTotalTemplates(0);
    setSelectedCategoryId(null);
    setSelectedLanguageId(null);
    setDebouncedSearch('');
    setCurrentPage(1);
    hasErrorRef.current = false;
  };

  const handleSelectCategory = (categoryId: number) => {
    setTemplates([]);
    setTotalTemplates(0);
    setSelectedCategoryId(categoryId);
    setSelectedLanguageId(null);
    setDebouncedSearch('');
    setCurrentPage(1);
    hasErrorRef.current = false;
  };

  const handleBackToCategories = () => {
    setTemplates([]);
    setTotalTemplates(0);
    setSelectedCategoryId(null);
    setSelectedLanguageId(null);
    setDebouncedSearch('');
    setCurrentPage(1);
    hasErrorRef.current = false;
  };

  useEffect(() => {
		const newTenantId = selectedTenant.value;
		if (newTenantId) {
      setTemplates([]);
      setTotalTemplates(0);
      setCurrentPage(1);
    }
	}, [selectedTenant]);

  useEffect(() => {
    filtersFetchedRef.current = false;
    fetchedCategoriesRef.current = null;
    isFetchingRef.current = false;
    hasErrorRef.current = false;
  }, [tenantIdToPass]);

  useEffect(() => {
    setCurrentPage(1);
    hasErrorRef.current = false;
  }, [activeTab, debouncedSearch, selectedLanguageId, sortOrder, tenantIdToPass]);

  useEffect(() => {
    if (viewMode === 'list') {
      if (activeTab === 'published') {
        if (selectedCategoryId === null) {
          fetchCategories();
          if (debouncedSearch && debouncedSearch.trim().length > 0) {
            fetchTemplates();
          }
        } else {
          fetchFilters();
          fetchTemplates();
        }
      } else {
        fetchTemplates();
      }
    }
  }, [
    activeTab,
    selectedCategoryId,
    debouncedSearch,
    selectedLanguageId,
    sortOrder,
    currentPage,
    tenantIdToPass,
    viewMode,
    fetchCategories,
    fetchTemplates,
    fetchFilters
  ]);

  const isSearchActiveOnCategoryPage = activeTab === 'published' && selectedCategoryId === null && debouncedSearch.trim().length > 0;
  const isCategoryView = activeTab === 'published' && selectedCategoryId === null && debouncedSearch.trim().length === 0;
  const isPublishedCategoryTemplatesView = activeTab === 'published' && selectedCategoryId !== null;
  const isPublishedNoTenant = isSuperAdmin && activeTab === 'published' && !selectedTenant?.value;

  const showBreadcrumb = isPublishedCategoryTemplatesView || isSearchActiveOnCategoryPage;

  return (
    <Spin spinning={loading}>
      <div className={`pm-container phishing-manager-wrapper ${styles.container}`}>
        {viewMode === 'edit_form' ? (
          <PrefilledForm
            templateDetails={templateDetails}
            isPublished={activeTab === 'published'}
            onBack={handleBackToList}
            setToast={setToast}
          />
        ) : (
          <>
            {isSuperAdmin && (
              <div className={styles.tabSwitcherWrapper}>
                <StatusTabSwitcher activeTab={activeTab} onChange={handleTabChange} />
              </div>
            )}

            {showBreadcrumb && (
              <div className={styles.breadcrumbWrapper}>
                <div className={styles.breadcrumbContent}>
                  <div className={styles.breadcrumbItem}>
                    <span
                      onClick={handleBackToCategories}
                      className={styles.breadcrumbLink}
                      style={
                        isSearchActiveOnCategoryPage
                          ? { textDecoration: 'underline', cursor: 'pointer' }
                          : undefined
                      }
                    >
                      {t('phishingTemplateManager.allTemplates')}
                    </span>
                  </div>

                  {isPublishedCategoryTemplatesView && (
                    <>
                      <div className={styles.breadcrumbChevron}>
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M4.5 9L7.5 6L4.5 3" stroke="var(--Colors-text-midnight, #001738)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </div>

                      <div className={styles.breadcrumbItem}>
                        <span className={styles.breadcrumbCurrent}>
                          {selectedCategoryObj?.name || t('phishingTemplateManager.category')}
                        </span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}

            {isPublishedCategoryTemplatesView ? (
              <PublishedFilterToolbar
                categoryTitle={selectedCategoryObj?.name || t('phishingTemplateManager.categoryTemplates')}
                searchQuery={debouncedSearch}
                onSearchChange={setDebouncedSearch}
                selectedLanguageId={selectedLanguageId}
                onLanguageChange={setSelectedLanguageId}
                sortOrder={sortOrder}
                onSortChange={setSortOrder}
                viewMode={viewLayoutMode}
                onViewModeChange={setViewLayoutMode}
                languages={availableLanguages}
              />
            ) : (
              <div className={`${styles.defaultToolbar} ${isMobile ? styles.mobile : ''}`}>
                <div className={styles.titleWithIcon}>
                  <Icon type="listColored" />
                  <h2 className={styles.sectionTitle}>
                    {activeTab === 'published'
                      ? isCategoryView
                        ? `${t('phishingTemplateManager.categories')} (${categories.length})`
                        : `${t('phishingTemplateManager.allTemplates')} (${totalTemplates})`
                      : `${t('phishingTemplateManager.unpublished')} (${totalTemplates})`}
                  </h2>
                </div>

                <div className={styles.searchWrapper}>
                  <DebounceInput
                    debounceSearch={debouncedSearch}
                    placeholder={t('phishingTemplateManager.searchPlaceholder')}
                    setDebouncedSearch={setDebouncedSearch}
                    className="phishing-custom-debounce-search"
                    showClearOption
                  />
                </div>
              </div>
            )}

            {isPublishedNoTenant ? (
              <EmptyState
                title={t('phishingManager.emptyState.noTenant.title')}
                description={[
                  t('phishingManager.emptyState.noTenant.description1'),
                  t('phishingManager.emptyState.noTenant.description2')
                ]}
                actionLabel={null}
              />
            ) : isCategoryView ? (
              categories.length === 0 && !loading ? (
                <EmptyState
                  title={t('phishingTemplateManager.emptyState.noCategory.title')}
                  description={[
                    t('phishingTemplateManager.emptyState.noCategory.description1'),
                    t('phishingTemplateManager.emptyState.noCategory.description2')
                  ]}
                  actionLabel={null}
                />
              ) : isMobile ? (
                <div className={styles.mobileListContainer}>
                  {categories.map((cat) => (
                    <CategoryCard
                      key={cat.id}
                      category={cat}
                      onViewTemplates={handleSelectCategory}
                    />
                  ))}
                </div>
              ) : (
                <div className={styles.gridFourColumns}>
                  {categories.map((cat) => (
                    <CategoryCard
                      key={cat.id}
                      category={cat}
                      onViewTemplates={handleSelectCategory}
                    />
                  ))}
                </div>
              )
            ) : templates.length === 0 && !loading ? (
              <EmptyState
                title={
                  activeTab === 'unpublished'
                    ? t('phishingTemplateManager.emptyState.noUnpublished.title')
                    : t('phishingTemplateManager.emptyState.noTemplates.title')
                }
                description={[
                  activeTab === 'unpublished'
                    ? t('phishingTemplateManager.emptyState.noUnpublished.description1')
                    : t('phishingTemplateManager.emptyState.noTemplates.description1')
                ]}
                actionLabel={null}
              />
            ) : isMobile ? (
              <InfinityScrollWrapper
                dataLength={templates.length}
                hasMore={templates.length < totalTemplates}
                onLoadMore={() => setCurrentPage((prev) => prev + 1)}
                height={600}
                scrollableId="template-scroll-container"
                loaderText={t('phishingTemplateManager.loaderText')}
                endMessageText={t('phishingTemplateManager.endMessageText')}
              >
                <div className={styles.mobileListContainer}>
                  {templates.map((template) =>
                    isPublishedCategoryTemplatesView && viewLayoutMode === 'list' ? (
                      <TemplateListCard
                        key={template.id}
                        data={template}
                        onReview={handleReviewClick}
                        onEdit={handleDirectEditClick}
                        isSuperAdmin={isSuperAdmin}
                      />
                    ) : (
                      <TemplateCard
                        key={template.id}
                        selected={false}
                        isLibrary={true}
                        onSelect={() => handleReviewClick(template.id)}
                        template={{
                          value: template.id,
                          label: template.title,
                          difficulty: template.difficulty,
                          img: template.img_path,
                          categories: template.categories || [],
                          languages: []
                        }}
                      />
                    )
                  )}
                </div>
              </InfinityScrollWrapper>
            ) : isPublishedCategoryTemplatesView && viewLayoutMode === 'list' ? (
              <div className={styles.gridTwoColumns}>
                {templates.map((template) => (
                  <TemplateListCard
                    key={template.id}
                    data={template}
                    onReview={handleReviewClick}
                    onEdit={handleDirectEditClick}
                    isSuperAdmin={isSuperAdmin}
                  />
                ))}
              </div>
            ) : (
              <div className={styles.gridFourColumns}>
                {templates.map((template) => (
                  <TemplateCard
                    key={template.id}
                    selected={false}
                    isLibrary={true}
                    onSelect={() => handleReviewClick(template.id)}
                    template={{
                      value: template.id,
                      label: template.title,
                      difficulty: template.difficulty,
                      img: template.img_path,
                      categories: template.categories || [],
                      languages: []
                    }}
                  />
                ))}
              </div>
            )}

            {!isCategoryView && !isMobile && totalTemplates > PAGE_SIZE && templates.length > 0 && (
              <div className={styles.paginationContainer}>
                <PaginationElement
                  total={totalTemplates}
                  pageSize={PAGE_SIZE}
                  current={currentPage}
                  onChange={(page) => setCurrentPage(page)}
                />
              </div>
            )}
          </>
        )}

        <LibraryModal
          visible={modalVisible}
          loading={modalLoading}
          templateDetails={templateDetails}
          isPublished={activeTab === 'published'}
          onClose={handleCloseModal}
          onEdit={handleSaveAndEdit}
          onLanguageChange={handleLanguageChange}
        />
      </div>
    </Spin>
  );
});

export default PhishingTemplateManager;